import {
    Injectable, inject
} from "@angular/core";
import {
    JiraServiceDeskTicket, RequestUtil, SupportSubmission
} from "@shopliftr/common-js/shared";
import {
    IInternalApiClient, InternalApiRequestService
} from "@shopliftr/common-ng";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

/**
 * API client service for submitting support tickets.
 *
 * Implements the API Client Service pattern by providing a focused interface
 * for support ticket submission against the Internal API. This service is provided
 * at the root level and should be injected into components or services that need
 * to submit support requests to the Jira Service Desk endpoint.
 *
 * **Service Pattern:** API Client Service
 *
 * **Provision:** Singleton service provided in 'root'
 *
 * @implements {IInternalApiClient} Base internal API client pattern (basePath only)
 *
 * @example
 * ```typescript
 * // Inject into a component or service
 * private readonly _supportApiClientService = inject(SupportApiClientService);
 *
 * // Submit a support ticket
 * const submission = new SupportSubmission();
 * submission.subject = "Login issue";
 * submission.description = "Unable to log in since yesterday.";
 *
 * this._supportApiClientService.submitTicket$(submission).subscribe(ticket => {
 *     console.log('Ticket created:', ticket.issueKey);
 * });
 * ```
 */
@Injectable({ providedIn: "root" })
export class SupportApiClientService implements IInternalApiClient {
    /**
     * Base API path for support submission endpoints.
     */
    readonly basePath = "support/submit";

    /**
     * Internal API request service for making HTTP requests.
     */
    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Submits a support ticket to the Jira Service Desk via the Internal API.
     *
     * @param submission The support submission data to send to the API
     * @returns Observable that emits the created {@link JiraServiceDeskTicket} from the API response
     */
    submitTicket$(submission: SupportSubmission): Observable<JiraServiceDeskTicket> {
        const request = RequestUtil.createSubmitRequest(submission);
        return this._internalApiRequestService
            .submit$<SupportSubmission, JiraServiceDeskTicket>(request, this.basePath)
            .pipe(map((response) => response.payload));
    }
}
